import { Injectable } from '@angular/core';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { HelpdeskTaskQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-query.dto';
import type { HelpdeskTaskRescheduleInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-reschedule.dto';
import type { HelpdeskTaskPageInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-page.dto';
import type { HelpdeskTaskSearchQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-search-query.dto';
import type { HelpdeskChangeStatusFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-change-status-form.dto';
import type { HelpdeskDelegateFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-delegate-form.dto';
import type { FactMinInterface } from '@zaoblako/shared/common/dtos/fact-min.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import type { HelpdeskTaskReportQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-query.dto';
import type { HelpdeskTaskReportItemInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-item.dto';
import type { HelpdeskTaskReportSumsInterface } from '@zaoblako/shared/modules/helpdesk/dto/report/helpdesk-task-report-sums.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import type { HelpdeskRequiredFieldsFindInterface } from '@zaoblako/shared/modules/saas/dtos/helpdesk-required-fields/helpdesk.required.fields.find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { CommentaryFileFindInterface } from '@zaoblako/shared/modules/storage/dtos/commentary-file-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HelpDeskTaskService extends BaseService {
  protected baseUrl = '/helpdesk/task';

  public search(queryParams?: HelpdeskTaskSearchQueryInterface): Observable<HelpdeskTaskPageInterface> {
    return this.http.get<HelpdeskTaskPageInterface>(`${this.baseUrl}/search`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public findWithPagination(queryParams: HelpdeskTaskQueryInterface): Observable<HelpdeskTaskPageInterface> {
    return this.http.get<HelpdeskTaskPageInterface>(this.baseUrl, { params: this.objectToHttpParams(queryParams) });
  }

  public changeStatus(
    id: number,
    body: HelpdeskChangeStatusFormInterface
  ): Observable<HelpdeskTaskStatusFindInterface> {
    return this.http.put<HelpdeskTaskStatusFindInterface>(`${this.baseUrl}/${id}/status`, body);
  }

  public delegate(id: number, body: HelpdeskDelegateFormInterface): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${id}/delegate`, body);
  }

  public getMembers(taskId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${taskId}/participants`);
  }

  public getStatuses(): Observable<HelpdeskTaskStatusFindInterface[]> {
    return this.http.get<HelpdeskTaskStatusFindInterface[]>(`${this.baseUrl}/status`);
  }

  public getTasksReport(queryParams: HelpdeskTaskReportQueryInterface): Observable<HelpdeskTaskReportItemInterface[]> {
    return this.http.get<HelpdeskTaskReportItemInterface[]>('/helpdesk/report/task', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getSumsReport(queryParams: HelpdeskTaskReportQueryInterface): Observable<HelpdeskTaskReportSumsInterface> {
    return this.http.get<HelpdeskTaskReportSumsInterface>('/helpdesk/report/task/sums', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getTasksReportExcel(queryParams: HelpdeskTaskReportQueryInterface): Observable<FileFindInterface> {
    return this.http.get<FileFindInterface>('/helpdesk/report/task/excel', {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getFactTime(id: number): Observable<FactMinInterface> {
    return this.http.get<FactMinInterface>(`${this.baseUrl}/${id}/fact-time`);
  }

  public getHistory(id: number, queryParams: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getFiles(taskId: number): Observable<CommentaryFileFindInterface[]> {
    return this.http.get<CommentaryFileFindInterface[]>(`${this.baseUrl}/${taskId}/files`);
  }

  public getRequiredFields(): Observable<HelpdeskRequiredFieldsFindInterface> {
    return this.http.get<HelpdeskRequiredFieldsFindInterface>(`${this.baseUrl}/required-fields`);
  }

  public changeRequiredFields(
    body: HelpdeskRequiredFieldsFindInterface
  ): Observable<HelpdeskRequiredFieldsFindInterface> {
    return this.http.put<HelpdeskRequiredFieldsFindInterface>(`${this.baseUrl}/required-fields`, body);
  }

  public toggleFavorite(taskId: number): Observable<HelpdeskTaskFindInterface> {
    return this.http.post<HelpdeskTaskFindInterface>(`${this.baseUrl}/${taskId}/toggle-favorite`, {});
  }

  public reschedule(taskId: number, body: HelpdeskTaskRescheduleInterface): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${taskId}/reschedule`, body);
  }

  public createReminder(taskId: number, params: ReminderFormInterface): Observable<ReminderFindInterface> {
    return this.http.post<ReminderFindInterface>(`${this.baseUrl}/${taskId}/reminder`, params);
  }
}
